import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TableActions from "./table-actions";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { TableLoadingSkeleton } from "src/shared/components/tables/table-loading-skeleton";
import {
  Button,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
  TablePagination,
  TextField,
  FormControl,
  IconButton,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { NoData } from "src/shared/components/tables/no-data";
import ClearIcon from "@mui/icons-material/Clear";
import { IEmployee } from "src/types/employees";
import AddBoxIcon from "@mui/icons-material/AddBox";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useRecoilState } from "recoil";
import { authState } from "src/store/auth";
import CustomAutocomplete from "src/shared/components/custom-auto-complete";
import { useGetLocationsList } from "src/api/locations";
import { downloadCsvFile } from "src/utils";
import { rolesList } from "../../add-edit-employee/constants";
import { useGetCitiesList } from "src/api/general";
import { serviceLabels } from "src/constants/general";
import { Menu } from "@mui/material";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import { doc, onSnapshot } from "firebase/firestore";
import { db } from "src/firebase";

const tableHeadText = [
  "National ID",
  "Name in English",
  "Name in Arabic",
  "Role",
  "Cities",
  "Type",
  "Email",
  "Mobile Number",
  "Actions",
];

const exportHeads = [
  "National ID",
  "Name in English",
  "Name in Arabic",
  "Nationality",
  "Role",
  "Cities",
  "Locations",
  "Services",
  "Type",
  "Support",
  "Email",
  "Mobile Number",
  "Secondary Mobile Number",
];

const ActiveEmployeesTable = ({
  rowsList,
  isLoading,
  fetchActiveUsers,
}: {
  rowsList: IEmployee[];
  isLoading: boolean;
  fetchActiveUsers: () => {};
}) => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  // Initialize state from URL
  const searchByParam = searchParams.get("search") ?? "";
  const roleParam = searchParams.get("role") ?? "all";
  const pageParam = Number(searchParams.get("page")) || 0;
  const typeParam = searchParams.get("type") ?? "all";
  const accountTypeParam = searchParams.get("accountType") ?? "all";
  const modeParam = searchParams.get("mode") ?? "all";
  const locationIdParam = searchParams.get("locationId");
  const cityParam = searchParams.get("cityId");

  const [page, setPage] = useState(pageParam);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchBy, setSearchBy] = useState(searchByParam);
  const [tempSearchBy, setTempSearchBy] = useState<string>(searchByParam);
  const [role, setRole] = useState(roleParam);
  const [tempRole, setTempRole] = useState(roleParam);
  const [auth] = useRecoilState(authState);
  const [type, setType] = useState(typeParam);
  const [tempType, setTempType] = useState(typeParam);
  const [accountType, setAccountType] = useState(accountTypeParam);
  const [tempAccountType, setTempAccountType] = useState(accountTypeParam);
  const [mode, setMode] = useState(modeParam);
  const [tempMode, setTempMode] = useState(modeParam);
  const [tempLocation, setTempLocation] = useState<any>(
    locationIdParam ? { id: locationIdParam } : null,
  );
  const [location, setLocation] = useState<any>(
    locationIdParam ? { id: locationIdParam } : null,
  );
  const [tempCityId, setTempCityId] = useState<string | null>(cityParam);
  const [cityId, setCityId] = useState<string | null>(cityParam);
  const [nationalities, setNationalities] = useState<any[]>([]);

  const { permissions } = auth;
  const userRole = auth?.user?.roleType;
  const prev = useRef({
    role,
    type,
    accountType,
    mode,
    location,
    cityId,
    searchBy,
  });

  // APIS
  const { data: locationsData } = useGetLocationsList();
  const { data: citiesData } = useGetCitiesList({
    type: "active",
  });

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // const handleNavigate = () => {
  //   navigate("/employees/active/add");
  // };

  const handleChangeRole = (e: SelectChangeEvent) => {
    setTempRole(e.target.value);
  };

  const handleChangeType = (e: SelectChangeEvent) => {
    setTempType(e.target.value);
  };

  const handleChangeAccountType = (e: SelectChangeEvent) => {
    setTempAccountType(e.target.value);
  };

  const handleChangeModeType = (e: SelectChangeEvent) => {
    setTempMode(e.target.value);
  };

  const handleSearch = (searchedVal: string) => {
    setTempSearchBy(searchedVal);
  };

  const clearSearch = () => {
    setTempSearchBy("");
  };

  const handleChangeCity = (
    event: React.SyntheticEvent,
    newValue: any | null,
  ) => {
    setTempCityId(newValue?.id ?? null);
  };

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const isAddMenuOpen = Boolean(anchorEl);

  const handleOpenAddMenu = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleCloseAddMenu = () => {
    setAnchorEl(null);
  };

  const handleNavigate = (route: string) => {
    handleCloseAddMenu();
    navigate(route);
  };

  const handleApply = () => {
    setSearchBy(tempSearchBy);
    setRole(tempRole);
    setType(tempType);
    setMode(tempMode);
    setLocation(tempLocation);
    setCityId(tempCityId);
    setAccountType(tempAccountType);
  };

  const handleResetFilers = () => {
    setPage(0);
    setRole("all");
    setType("all");
    setMode("all");
    setSearchBy("");
    setLocation(null);
    setCityId(null);
    setAccountType("all");

    setTempRole("all");
    setTempType("all");
    setTempMode("all");
    setTempSearchBy("");
    setTempLocation(null);
    setTempCityId(null);
    setTempAccountType("all");
  };

  const handleChangeLocation = (event: React.SyntheticEvent, newValue: any) => {
    setTempLocation(newValue);
    setTempCityId(null);
  };

  const handleSearchAndFilters = () => {
    let searchAndFiltersRows = [...rowsList];
    //search by name, email, national id or Phone
    if (searchBy.trim()) {
      const filteredRows = searchAndFiltersRows.filter((row: any) => {
        return (
          row.name?.toLowerCase().includes(searchBy.toLowerCase()) ||
          row.arabicName?.toLowerCase().includes(searchBy.toLowerCase()) ||
          row.email?.toLowerCase().includes(searchBy.toLowerCase()) ||
          row.nationalId?.toLowerCase().includes(searchBy.toLowerCase()) ||
          row.phone?.toLowerCase().includes(searchBy.toLowerCase())
        );
      });
      searchAndFiltersRows = filteredRows;
    }
    //filter by role
    if (role === "Super Admin") {
      const filteredRows = searchAndFiltersRows.filter((row: any) => {
        return row.isSuperAdmin;
      });
      searchAndFiltersRows = filteredRows;
    } else if (role === "Admin") {
      const filteredRows = searchAndFiltersRows.filter((row: any) => {
        return row.roles.includes("Admin");
      });
      searchAndFiltersRows = filteredRows;
    } else if (!!role && role !== "all") {
      const filteredRows = searchAndFiltersRows.filter((row: any) => {
        return row.locationType === role;
      });
      searchAndFiltersRows = filteredRows;
    }
    //filter by type
    if (type !== "all") {
      const filteredRows = searchAndFiltersRows.filter((row) => {
        return row.employeeType === type;
      });
      searchAndFiltersRows = filteredRows;
    }
    //filter by account type
    if (accountType !== "all") {
      const filteredRows = searchAndFiltersRows.filter((row) => {
        return row.accountType === accountType;
      });
      searchAndFiltersRows = filteredRows;
    }
    //filter by mode
    if (mode !== "all") {
      const filteredRows = searchAndFiltersRows.filter((row) => {
        return mode === "support" ? row.isSupportMode : !row.isSupportMode;
      });
      searchAndFiltersRows = filteredRows;
    }
    // filter by city
    if (cityId) {
      searchAndFiltersRows = searchAndFiltersRows.filter((row: any) =>
        row.cities?.some((item: any) => item.id === cityId),
      );
    }
    //filter by location
    if (location) {
      const filteredRows = searchAndFiltersRows.filter((row) => {
        return row.allowedLocationsId?.includes(location.id);
      });
      searchAndFiltersRows = filteredRows;
    }
    return searchAndFiltersRows;
  };

  const searchAndFiltersList = handleSearchAndFilters();

  const handleExportActiveEmployees = () => {
    const exportData =
      searchAndFiltersList?.flatMap((item) => {
        const employeeRole = item?.isSuperAdmin
          ? "Super Admin"
          : item?.isAdmin
            ? "Admin"
            : item?.locationType;

        const Nationality =
          nationalities.find((nat) => nat.id === item.nationalityId)?.labelAr ||
          "";

        const baseData = {
          "National ID": item?.nationalId,
          "Name in English": item?.name,
          "Name in Arabic": item?.arabicName,
          Nationality: Nationality,
          Role: employeeRole,
          Cities: item?.cities?.map((city: any) => city.name).join(" - "),
          Type: item?.employeeType
            ? item.employeeType.charAt(0).toUpperCase() +
              item.employeeType.slice(1)
            : "",
          Support: item?.isSupportMode ? "YES" : "NO",
          Email: item?.email,
          "Mobile Number": item?.phone,
          "Secondary Mobile Number": item?.phone2,
        };

        // If allowedLocationServices exists and has data,
        // create one record for each location/service
        if (item?.allowedLocationServices?.length) {
          return item.allowedLocationServices.map((locationService: any) => {
            const location = locationsData?.locations?.find(
              (location: any) => location.id === locationService.locationId,
            );

            return {
              ...baseData,
              Locations: location?.nameEn || "",
              Services:
                locationService?.serviceTypes
                  ?.map(
                    (serviceType: string) =>
                      serviceLabels[serviceType] || serviceType,
                  )
                  .join(" - ") || "",
            };
          });
        }

        // Otherwise create one record for each allowed location
        return (
          item?.allowedLocations?.map((locationName: string) => ({
            ...baseData,
            Locations: locationName,
            Services: "",
          })) || [
            {
              ...baseData,
              Locations: "",
              Services: "",
            },
          ]
        );
      }) || [];

    downloadCsvFile(exportHeads, exportData, "Active Accounts List");
  };

  const paginatedVisibleRows = useMemo(
    () =>
      searchAndFiltersList.slice(
        page * rowsPerPage,
        page * rowsPerPage + rowsPerPage,
      ),
    [
      page,
      rowsPerPage,
      rowsList,
      searchBy,
      role,
      type,
      accountType,
      mode,
      location,
      cityId,
    ],
  );

  const sortedLocations = useMemo(() => {
    return (
      locationsData?.locations.sort((a: any, b: any) =>
        a.nameEn?.localeCompare(b.nameEn),
      ) ?? []
    );
  }, [locationsData]);

  const filteredCities = useMemo(() => {
    if (!citiesData?.data) return [];

    if (!tempLocation) {
      return citiesData.data;
    }

    const availableLocations = tempLocation ? [tempLocation] : sortedLocations;

    const availableLocationsCitiesIds = availableLocations.map(
      (loc: any) => loc.cityId,
    );

    return citiesData.data.filter((city) =>
      availableLocationsCitiesIds.includes(city.id),
    );
  }, [tempLocation, citiesData, sortedLocations]);

  const selectedCity =
    filteredCities.find((city) => city.id === tempCityId) ?? null;

  const emptyDataArr = searchAndFiltersList.length === 0 && !isLoading;

  useEffect(() => {
    const changed =
      prev.current.role !== role ||
      prev.current.type !== type ||
      prev.current.accountType !== accountType ||
      prev.current.mode !== mode ||
      prev.current.location !== location ||
      prev.current.cityId !== cityId ||
      prev.current.searchBy !== searchBy;

    if (changed) {
      setPage(0);
    }

    prev.current = {
      role,
      type,
      accountType,
      mode,
      location,
      cityId,
      searchBy,
    };
  }, [role, type, accountType, mode, location, searchBy, cityId]);

  // Restore location filter
  useEffect(() => {
    const locationId = searchParams.get("locationId");
    if (locationId && sortedLocations.length) {
      const found = sortedLocations.find((loc: any) => loc.id === locationId);
      setLocation(found);
      setTempLocation(found);
    }
  }, [sortedLocations]);

  // Sync URL when filters change
  useEffect(() => {
    setSearchParams({
      search: searchBy,
      role,
      type,
      accountType,
      mode,
      page: page.toString(),
      locationId: location?.id ?? "",
      cityId: cityId ?? "",
    });
  }, [searchBy, role, type, accountType, mode, page, location, cityId]);

  // Fetch kafd categories enum
  useEffect(() => {
    let unsubscribe: (() => void) | undefined;

    const fetchData = async () => {
      try {
        const docRef = doc(db, "settings", "sunmi_nationalities");

        unsubscribe = onSnapshot(docRef, (docSnap) => {
          if (docSnap.exists()) {
            const docData = docSnap.data();

            setNationalities([...(docData?.items || [])]);
          } else {
            console.warn("No such enum document found!");
          }
        });
      } catch (error) {
        console.error("Error fetching enum:", error);
      }
    };

    fetchData();

    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, []);

  return (
    <>
      <Stack mb={2.2} alignItems={"flex-end"}>
        {permissions?.arsannEmployees.create &&
          userRole !== "HR" &&
          userRole !== "Finance" && (
            <>
              <Button
                variant="contained"
                sx={{ gap: 0.5 }}
                onClick={handleOpenAddMenu}
                endIcon={<ArrowDropDownIcon />}
              >
                <AddBoxIcon fontSize="small" sx={{ opacity: 0.7 }} />
                Add New Account
              </Button>

              <Menu
                anchorEl={anchorEl}
                open={isAddMenuOpen}
                onClose={handleCloseAddMenu}
                anchorOrigin={{
                  vertical: "bottom",
                  horizontal: "right",
                }}
                transformOrigin={{
                  vertical: "top",
                  horizontal: "right",
                }}
                slotProps={{
                  paper: {
                    sx: {
                      width: anchorEl?.clientWidth,
                    },
                  },
                }}
              >
                <MenuItem
                  onClick={() => handleNavigate("/employees/active/add")}
                >
                  Employee Account
                </MenuItem>

                <MenuItem
                  onClick={() => handleNavigate("/employees/active/add-sunmi")}
                >
                  Sunmi Account
                </MenuItem>
              </Menu>
            </>
          )}
      </Stack>

      <Stack direction={{ xs: "column", md: "row" }} gap={"10px"} mb={2.2}>
        <Stack direction={"row"} gap={"10px"} flexWrap={"wrap"} flexGrow={1}>
          <TextField
            size="small"
            sx={{ width: { xs: "100%", sm: "230px" } }}
            placeholder="Search"
            value={tempSearchBy}
            onChange={(event) => handleSearch(event.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment
                  position="end"
                  sx={{ opacity: !tempSearchBy ? 0 : 1 }}
                >
                  <IconButton size="small" onClick={clearSearch}>
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          <FormControl size="small" sx={{ minWidth: "180px" }}>
            <InputLabel id="demo-simple-select-label">Role</InputLabel>
            <Select
              labelId="demo-simple-select-label"
              id="demo-simple-select"
              value={tempRole}
              label="Role"
              onChange={handleChangeRole}
            >
              <MenuItem value={"all"}>All</MenuItem>
              {rolesList.map((role) => (
                <MenuItem key={role} value={role}>
                  {role}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: "130px" }}>
            <InputLabel id="demo-simple-select-label">Account Type</InputLabel>
            <Select
              labelId="demo-simple-select-label"
              id="demo-simple-select"
              value={tempAccountType}
              label="Account Type"
              onChange={handleChangeAccountType}
            >
              <MenuItem value={"all"}>All</MenuItem>
              <MenuItem value={"regular"}>Employee</MenuItem>
              <MenuItem value={"sunmi"}>Sunmi</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: "130px" }}>
            <InputLabel id="demo-simple-select-label">Mode</InputLabel>
            <Select
              labelId="demo-simple-select-label"
              id="demo-simple-select"
              value={tempMode}
              label="Mode"
              onChange={handleChangeModeType}
            >
              <MenuItem value={"all"}>All</MenuItem>
              <MenuItem value={"support"}>Support</MenuItem>
              <MenuItem value={"regular"}>Regular</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: "130px" }}>
            <InputLabel id="demo-simple-select-label">Type</InputLabel>
            <Select
              labelId="demo-simple-select-label"
              id="demo-simple-select"
              value={tempType}
              label="Type"
              onChange={handleChangeType}
            >
              <MenuItem value={"all"}>All</MenuItem>
              <MenuItem value={"permanent"}>Permanent</MenuItem>
              <MenuItem value={"temporary"}>Temporary</MenuItem>
            </Select>
          </FormControl>
          {/* locations */}
          <CustomAutocomplete
            size="small"
            sx={{ width: "200px" }}
            fullWidth
            disablePortal
            id="combo-box-demo"
            options={sortedLocations}
            getOptionLabel={(option: any) => option.nameEn ?? ""}
            getOptionKey={(option: any) => option.id}
            value={tempLocation}
            onChange={handleChangeLocation}
            renderInput={(params: any) => (
              <TextField fullWidth {...params} label={"Location"} />
            )}
          />
          {/* city */}
          <CustomAutocomplete
            key={tempCityId ?? "city-autocomplete"}
            sx={{ width: { xs: "100%", sm: "170px" } }}
            size="small"
            disablePortal
            id="combo-box-demo"
            options={filteredCities}
            getOptionLabel={(option) => option.nameEn}
            getOptionKey={(option) => `${option.id}`}
            value={selectedCity}
            onChange={handleChangeCity}
            renderInput={(params: any) => (
              <TextField fullWidth {...params} label={"City"} />
            )}
          />
        </Stack>
        <Stack direction={"row"} gap={1}>
          <Button onClick={handleResetFilers}>Clear</Button>
          <Button
            variant="contained"
            onClick={handleApply}
            sx={{ width: "100px" }}
          >
            Apply
          </Button>
        </Stack>
      </Stack>
      <Stack alignItems={"flex-end"} mb={2.2}>
        <Button variant="contained" onClick={handleExportActiveEmployees}>
          Export
        </Button>
      </Stack>
      <TableContainer
        sx={{
          boxShadow: "0px 14px 44px 0px rgba(0, 0, 0, 0.35)",
          padding: "30px",
        }}
      >
        <Table sx={{ minWidth: 650 }} aria-label="simple table">
          <TableHead>
            <TableRow>
              {tableHeadText.map((item) => (
                <TableCell
                  key={item}
                  sx={{
                    fontWeight: 600,
                    color: "#A1A5B7",
                  }}
                >
                  {item}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          {isLoading ? (
            <TableLoadingSkeleton rowsLength={7} cellsLength={9} />
          ) : (
            <TableBody>
              {paginatedVisibleRows?.map((row) => {
                const employeeRole = row?.isSuperAdmin
                  ? "Super Admin"
                  : row?.isAdmin
                    ? "Admin"
                    : row.locationType;
                return (
                  <TableRow
                    key={row.employeeId}
                    sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
                  >
                    <TableCell>{row.nationalId}</TableCell>
                    <TableCell>{row.name}</TableCell>
                    <TableCell>{row.arabicName}</TableCell>
                    <TableCell sx={{ maxWidth: "250px" }}>
                      {employeeRole}
                    </TableCell>
                    <TableCell>
                      {row.cities?.map((city: any) => city.name).join(", ")}
                    </TableCell>
                    <TableCell sx={{ textTransform: "capitalize" }}>
                      {row.employeeType}
                    </TableCell>
                    <TableCell>{row.email}</TableCell>
                    <TableCell>{row.phone}</TableCell>
                    <TableCell>
                      <TableActions
                        rowId={row.employeeId}
                        rowRole={employeeRole}
                        accountType={row.accountType}
                        fetchActiveUsers={fetchActiveUsers}
                      />
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          )}
        </Table>

        {emptyDataArr && <NoData />}

        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={searchAndFiltersList.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </TableContainer>
    </>
  );
};
export default ActiveEmployeesTable;
